import React, { FC, useEffect, useState } from "react";

import {
  Typography,
  Stack,
  Snackbar,
  Alert,
  Grid,
  Box,
  Button,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { DialogTextActionButton } from "../../Base/DialogTextActionButton";

import { useAppDispatch, useAppSelector } from "../../../store";
import {
  fetchTournoi,
  fetchAllRegistrations,
  changeTournamentStatsCollection,
  generateDossards,
  regenerateAllDossards,
  clearAllDossards,
  generatePlayersCSV,
  changeRegistrationPhase,
  changeCancellationMessage,
  cancelTournament,
  restoreCancelledTournament,
  getTournoi,
  getAreAllDossardsGenerated,
  getIsRegistrationPhaseClosed,
  getIsTournamentCancelled,
  getRegistrationPhase,
  changeTournamentAlertThresholds,
  fetchBoards,
} from "../../../features/tournoi/tournoiSlice";

import { RegistrationPhase } from "../../../types";
import EditableField from "../../Base/EditableField";
import MarkdownEditorField from "../../Base/MarkdownEditorField";
import { DEFAULT_CANCELLATION_MESSAGE } from "../../../constants/defaultCancellationMessage";
import AdminTournamentRefunds from "./AdminTournamentRefunds";

interface AdminTournamentProps {}

type PhaseConfig = {
  label: string;
  color: "default" | "error" | "warning" | "primary" | "success" | "info";
  nextPhase: RegistrationPhase | null;
  nextLabel: string | null;
  nextColor: "inherit" | "error" | "warning" | "primary" | "success" | "info";
  confirmMessage: string;
};

const PHASE_CONFIG: Record<RegistrationPhase, PhaseConfig> = {
  closed: {
    label: "Fermées",
    color: "error",
    nextPhase: "preRegistration",
    nextLabel: "Ouvrir la pré-inscription",
    nextColor: "warning",
    confirmMessage:
      "Ouvrir la pré-inscription ? Les admins et les clubs organisateurs pourront s'inscrire.",
  },
  preRegistration: {
    label: "Pré-inscription",
    color: "warning",
    nextPhase: "open",
    nextLabel: "Ouvrir à tous",
    nextColor: "success",
    confirmMessage:
      "Ouvrir les inscriptions à tous les joueurs ? Le paiement via Stripe sera activé.",
  },
  open: {
    label: "Ouvertes",
    color: "success",
    nextPhase: "closedForPlayers",
    nextLabel: "Fermer aux joueurs",
    nextColor: "info",
    confirmMessage:
      "Fermer les inscriptions aux joueurs ? Les admins pourront toujours inscrire à la demande.",
  },
  closedForPlayers: {
    label: "Fermées aux joueurs",
    color: "info",
    nextPhase: null,
    nextLabel: null,
    nextColor: "inherit",
    confirmMessage: "",
  },
  cancelled: {
    label: "Annulé",
    color: "error",
    nextPhase: null,
    nextLabel: null,
    nextColor: "inherit",
    confirmMessage: "",
  },
};

const AdminTournament: FC<AdminTournamentProps> = () => {
  const tournoi = useAppSelector(getTournoi);
  const dispatch = useAppDispatch();
  const [snackBarOpened, setSnackBarOpened] = useState(false);
  const [snackBarMessage, setSnackBarMessage] = useState("");
  const [snackBarSeverity, setSnackBarSeverity] = useState<"success" | "error">(
    "success"
  );
  const [isDossardLoading, setIsDossardLoading] = useState(false);
  const [isCSVLoading, setIsCSVLoading] = useState(false);
  const [isPhaseLoading, setIsPhaseLoading] = useState(false);
  const [confirmPhaseOpen, setConfirmPhaseOpen] = useState(false);
  const [confirmCancelOpen, setConfirmCancelOpen] = useState(false);
  const [confirmRestoreOpen, setConfirmRestoreOpen] = useState(false);
  const [isCancellationLoading, setIsCancellationLoading] = useState(false);
  const [localCancellationMessage, setLocalCancellationMessage] = useState("");

  const areAllDossardsGenerated = useAppSelector(getAreAllDossardsGenerated);
  const isRegistrationPhaseClosed = useAppSelector(getIsRegistrationPhaseClosed);
  const isTournamentCancelled = useAppSelector(getIsTournamentCancelled);
  const currentPhase = useAppSelector(getRegistrationPhase);
  const phaseConfig = currentPhase === "cancelled"
    ? null
    : PHASE_CONFIG[currentPhase];

  const defaultAlertThresholds = {
    cree: { "2": 60, "3": 20 },
    enCours: { "2": 50, "3": 100 },
  };

  const alertThresholds = tournoi.alertThresholds || defaultAlertThresholds;
  const [localAlertThresholds, setLocalAlertThresholds] =
    useState(alertThresholds);

  useEffect(() => {
    const loadData = async () => {
      await dispatch(fetchTournoi());
      await dispatch(fetchBoards());
      await dispatch(fetchAllRegistrations({ reload: false }));
    };
    loadData();
  }, [dispatch]);

  useEffect(() => {
    setLocalCancellationMessage(
      tournoi?.cancellationMessage?.trim() || DEFAULT_CANCELLATION_MESSAGE
    );
  }, [tournoi?.cancellationMessage]);

  const handleAlertThresholdChange = (
    state: "cree" | "enCours",
    nb: "2" | "3",
    value: number
  ) => {
    setLocalAlertThresholds((prev) => ({
      ...prev,
      [state]: { ...prev[state], [nb]: value },
    }));
  };

  const handleSaveAlertThresholds = async () => {
    await dispatch(
      changeTournamentAlertThresholds({
        tournamentId: tournoi.id,
        alertThresholds: localAlertThresholds,
      })
    );
    openSnackBar("Seuils d'alerte mis à jour", "success");
  };

  const openSnackBar = (
    message: string,
    severity: "success" | "error" = "success"
  ) => {
    setSnackBarMessage(message);
    setSnackBarSeverity(severity);
    setSnackBarOpened(true);
  };

  const handleCloseSnackBar = (
    event: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") return;
    setSnackBarOpened(false);
  };

  const handleDossardOperation = async (
    operation: () => any,
    successMessage: string
  ) => {
    setIsDossardLoading(true);
    try {
      const result = await dispatch(operation());
      if (result.error) {
        openSnackBar(`Erreur : ${result.payload || "Une erreur inattendue s'est produite"}`, "error");
      } else {
        openSnackBar(successMessage, "success");
        await dispatch(fetchAllRegistrations({ reload: true }));
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Une erreur inattendue s'est produite";
      openSnackBar(`Erreur : ${errorMessage}`, "error");
    } finally {
      setIsDossardLoading(false);
    }
  };

  const handleGenerateCSV = async () => {
    setIsCSVLoading(true);
    try {
      const result = await dispatch(generatePlayersCSV());
      if (generatePlayersCSV.fulfilled.match(result)) {
        openSnackBar("Fichier CSV généré avec succès", "success");
      } else {
        openSnackBar(`Erreur : ${result.payload || "Une erreur inattendue s'est produite"}`, "error");
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Une erreur inattendue s'est produite";
      openSnackBar(`Erreur : ${errorMessage}`, "error");
    } finally {
      setIsCSVLoading(false);
    }
  };

  const handlePhaseTransition = async () => {
    if (!phaseConfig?.nextPhase) return;
    setIsPhaseLoading(true);
    setConfirmPhaseOpen(false);
    try {
      const result = await dispatch(
        changeRegistrationPhase({ tournamentId: tournoi.id, phase: phaseConfig.nextPhase })
      );
      if (changeRegistrationPhase.fulfilled.match(result)) {
        openSnackBar(
          `Phase mise à jour : ${PHASE_CONFIG[phaseConfig.nextPhase].label}`,
          "success"
        );
      } else {
        openSnackBar("Erreur lors du changement de phase", "error");
      }
    } catch (error) {
      openSnackBar("Erreur lors du changement de phase", "error");
    } finally {
      setIsPhaseLoading(false);
    }
  };

  const handleSaveCancellationMessage = async () => {
    setIsCancellationLoading(true);
    try {
      const result = await dispatch(
        changeCancellationMessage({
          tournamentId: tournoi.id,
          cancellationMessage: localCancellationMessage,
        })
      );
      if (changeCancellationMessage.fulfilled.match(result)) {
        openSnackBar("Message d'annulation enregistré", "success");
      } else {
        openSnackBar("Erreur lors de l'enregistrement du message", "error");
      }
    } catch {
      openSnackBar("Erreur lors de l'enregistrement du message", "error");
    } finally {
      setIsCancellationLoading(false);
    }
  };

  const handleCancelTournament = async () => {
    setIsCancellationLoading(true);
    setConfirmCancelOpen(false);
    try {
      const result = await dispatch(
        cancelTournament({
          tournamentId: tournoi.id,
          cancellationMessage: localCancellationMessage,
        })
      );
      if (cancelTournament.fulfilled.match(result)) {
        openSnackBar("Le tournoi a été marqué comme annulé", "success");
      } else {
        openSnackBar("Erreur lors de l'annulation du tournoi", "error");
      }
    } catch {
      openSnackBar("Erreur lors de l'annulation du tournoi", "error");
    } finally {
      setIsCancellationLoading(false);
    }
  };

  const handleRestoreTournament = async () => {
    setIsCancellationLoading(true);
    setConfirmRestoreOpen(false);
    try {
      const result = await dispatch(
        restoreCancelledTournament({ tournamentId: tournoi.id })
      );
      if (restoreCancelledTournament.fulfilled.match(result)) {
        openSnackBar("Le tournoi a été rétabli (inscriptions fermées aux joueurs)", "success");
      } else {
        openSnackBar("Erreur lors du rétablissement du tournoi", "error");
      }
    } catch {
      openSnackBar("Erreur lors du rétablissement du tournoi", "error");
    } finally {
      setIsCancellationLoading(false);
    }
  };

  return (
    <>
      <Stack direction="column" spacing={3} margin={2}>

        {/* ——— Phase des inscriptions ——— */}
        <Stack spacing={2}>
          <Typography variant="h6">Phase des inscriptions</Typography>
          {isTournamentCancelled ? (
            <Alert severity="error" variant="filled" sx={{ maxWidth: 720 }}>
              <strong>Tournoi annulé.</strong> Les inscriptions sont fermées pour
              tous. Le message ci-dessous est affiché sur la page d&apos;accueil.
            </Alert>
          ) : (
            <>
              <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                <Typography variant="body2" color="text.secondary">
                  Phase actuelle :
                </Typography>
                <Chip
                  label={phaseConfig?.label ?? ""}
                  color={phaseConfig?.color ?? "default"}
                  variant="filled"
                  sx={{ fontWeight: 600 }}
                />
              </Stack>

              {phaseConfig?.nextPhase && (
                <Box>
                  <Button
                    variant="contained"
                    color={phaseConfig.nextColor}
                    onClick={() => setConfirmPhaseOpen(true)}
                    disabled={isPhaseLoading}
                    startIcon={isPhaseLoading ? <CircularProgress size={16} /> : null}
                    sx={{ textTransform: "none" }}
                  >
                    {isPhaseLoading ? "Changement en cours..." : phaseConfig.nextLabel}
                  </Button>
                </Box>
              )}

              {currentPhase === "closed" && (
                <Alert severity="info" variant="outlined" sx={{ maxWidth: 600 }}>
                  En phase <strong>Fermées</strong>, personne ne peut s'inscrire. Passez en pré-inscription pour permettre aux clubs organisateurs et aux admins de s'inscrire, ou ouvrez directement à tous.
                </Alert>
              )}
              {currentPhase === "preRegistration" && (
                <Alert severity="warning" variant="outlined" sx={{ maxWidth: 600 }}>
                  <strong>Pré-inscription active</strong> — seuls les admins et les clubs organisateurs peuvent s'inscrire (via Stripe). Les autres joueurs voient un message d'attente.
                </Alert>
              )}
              {currentPhase === "open" && (
                <Alert severity="success" variant="outlined" sx={{ maxWidth: 600 }}>
                  <strong>Inscriptions ouvertes à tous.</strong> Les joueurs paient via Stripe, les admins inscrivent directement.
                </Alert>
              )}
              {currentPhase === "closedForPlayers" && (
                <Alert severity="info" variant="outlined" sx={{ maxWidth: 600 }}>
                  <strong>Inscriptions fermées aux joueurs.</strong> Les admins peuvent toujours inscrire à la demande.
                </Alert>
              )}
            </>
          )}
        </Stack>

        {/* ——— Annulation du tournoi ——— */}
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="h6">Annulation du tournoi</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 720 }}>
            Message affiché sur la page d&apos;accueil lorsque le tournoi est annulé.
            Vous pouvez le préparer à l&apos;avance puis activer l&apos;état « annulé ».
          </Typography>

          <MarkdownEditorField
            value={localCancellationMessage}
            onChange={setLocalCancellationMessage}
            label="Message d'annulation (Markdown)"
            helperText="Titres, listes, gras et liens sont pris en charge. Utilisez l'onglet Aperçu pour vérifier le rendu."
          />

          <Stack direction="row" spacing={2} flexWrap="wrap">
            <Button
              variant="outlined"
              onClick={() =>
                setLocalCancellationMessage(DEFAULT_CANCELLATION_MESSAGE)
              }
            >
              Réinitialiser le texte par défaut
            </Button>
            <Button
              variant="contained"
              onClick={handleSaveCancellationMessage}
              disabled={isCancellationLoading}
              startIcon={
                isCancellationLoading ? <CircularProgress size={16} /> : null
              }
            >
              Enregistrer le message
            </Button>
            {!isTournamentCancelled ? (
              <Button
                variant="contained"
                color="error"
                onClick={() => setConfirmCancelOpen(true)}
                disabled={isCancellationLoading}
              >
                Annuler le tournoi
              </Button>
            ) : (
              <Button
                variant="contained"
                color="warning"
                onClick={() => setConfirmRestoreOpen(true)}
                disabled={isCancellationLoading}
              >
                Rétablir le tournoi
              </Button>
            )}
          </Stack>
        </Stack>

        <AdminTournamentRefunds />

        <Stack direction="row" spacing={3}>
          <Typography>Collection Firestore pour les statistiques :</Typography>
          <EditableField
            label={tournoi?.statsCollectionName || "matchs"}
            type="string"
            onChange={async (newValue) => {
              const resAction = await dispatch(
                changeTournamentStatsCollection({
                  tournamentId: tournoi?.id,
                  statsCollectionName: newValue || "matchs",
                })
              );
              if (changeTournamentStatsCollection.fulfilled.match(resAction)) {
                openSnackBar("Collection des statistiques mise à jour");
              }
            }}
          />
        </Stack>

        {/* ——— Seuils d'alerte ——— */}
        <Stack spacing={2} sx={{ mt: 4 }}>
          <Typography variant="h6">
            Seuils d'alerte pour les matchs en attente
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="subtitle2">
                Matchs non lancés (CREE, DOUBLON, LIBERE)
              </Typography>
              <Box display="flex" gap={2} alignItems="center">
                <Typography>2 joueurs :</Typography>
                <input
                  type="number"
                  min={1}
                  value={localAlertThresholds.cree["2"]}
                  onChange={(e) =>
                    handleAlertThresholdChange("cree", "2", Number(e.target.value))
                  }
                  style={{ width: 60 }}
                />
                <Typography>min</Typography>
              </Box>
              <Box display="flex" gap={2} alignItems="center" mt={1}>
                <Typography>3 joueurs :</Typography>
                <input
                  type="number"
                  min={1}
                  value={localAlertThresholds.cree["3"]}
                  onChange={(e) =>
                    handleAlertThresholdChange("cree", "3", Number(e.target.value))
                  }
                  style={{ width: 60 }}
                />
                <Typography>min</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="subtitle2">
                Matchs en cours (ENCOURS)
              </Typography>
              <Box display="flex" gap={2} alignItems="center">
                <Typography>2 joueurs :</Typography>
                <input
                  type="number"
                  min={1}
                  value={localAlertThresholds.enCours["2"]}
                  onChange={(e) =>
                    handleAlertThresholdChange("enCours", "2", Number(e.target.value))
                  }
                  style={{ width: 60 }}
                />
                <Typography>min</Typography>
              </Box>
              <Box display="flex" gap={2} alignItems="center" mt={1}>
                <Typography>3 joueurs :</Typography>
                <input
                  type="number"
                  min={1}
                  value={localAlertThresholds.enCours["3"]}
                  onChange={(e) =>
                    handleAlertThresholdChange("enCours", "3", Number(e.target.value))
                  }
                  style={{ width: 60 }}
                />
                <Typography>min</Typography>
              </Box>
            </Grid>
          </Grid>
          <Button
            variant="contained"
            onClick={handleSaveAlertThresholds}
            sx={{ mt: 2, alignSelf: "flex-start" }}
          >
            Enregistrer les seuils d'alerte
          </Button>
        </Stack>

        {/* ——— Dossards ——— */}
        <Stack spacing={2} sx={{ mt: 4 }}>
          <Typography variant="h6">Gestion des dossards</Typography>
          <Typography variant="body2" color="text.secondary">
            Les dossards sont attribués par ordre décroissant du nombre de
            points, puis nom/prénom. Stockage : sous-collection{" "}
            <code>tournois/…/dossards</code> (indépendant entre tournois).
          </Typography>
          <Stack direction="row" spacing={2} flexWrap="wrap">
            <Button
              variant="contained"
              color="primary"
              onClick={() =>
                handleDossardOperation(generateDossards, "Dossards générés avec succès")
              }
              disabled={isDossardLoading}
              startIcon={isDossardLoading ? <CircularProgress size={16} /> : null}
            >
              {isDossardLoading ? "Génération..." : "Générer dossards manquants"}
            </Button>
            <Button
              variant="contained"
              color="secondary"
              onClick={() =>
                handleDossardOperation(
                  regenerateAllDossards,
                  "Tous les dossards ont été régénérés"
                )
              }
              disabled={isDossardLoading}
              startIcon={isDossardLoading ? <CircularProgress size={16} /> : null}
            >
              {isDossardLoading ? "Régénération..." : "Régénérer tous les dossards"}
            </Button>
            <Button
              variant="outlined"
              color="error"
              onClick={() =>
                handleDossardOperation(
                  clearAllDossards,
                  "Tous les dossards ont été supprimés"
                )
              }
              disabled={isDossardLoading}
              startIcon={isDossardLoading ? <CircularProgress size={16} /> : null}
            >
              {isDossardLoading ? "Suppression..." : "Supprimer tous les dossards"}
            </Button>
          </Stack>
        </Stack>

        {/* ——— Export CSV ——— */}
        <Stack spacing={2} sx={{ mt: 4 }}>
          <Typography variant="h6">
            Export et gestion des inscriptions
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Générez un fichier CSV des joueurs inscrits.
          </Typography>
          <Stack direction="row" spacing={2} flexWrap="wrap">
            <Button
              variant="contained"
              color="primary"
              onClick={handleGenerateCSV}
              disabled={!areAllDossardsGenerated || isCSVLoading}
              startIcon={isCSVLoading ? <CircularProgress size={16} /> : null}
            >
              {isCSVLoading ? "Génération..." : "Générer CSV des joueurs inscrits"}
            </Button>
          </Stack>
          {!areAllDossardsGenerated && (
            <Typography variant="body2" color="warning.main">
              Les dossards doivent être générés avant de pouvoir exporter le CSV.
            </Typography>
          )}
          {isRegistrationPhaseClosed && (
            <Typography variant="body2" color="info.main">
              Les inscriptions sont fermées aux joueurs. Les nouvelles inscriptions admin seront marquées comme "post-clôture".
            </Typography>
          )}
        </Stack>
      </Stack>

      {/* ——— Dialog confirmation changement de phase ——— */}
      <Dialog open={confirmPhaseOpen} onClose={() => setConfirmPhaseOpen(false)} maxWidth="xs">
        <DialogTitle>Confirmer le changement de phase</DialogTitle>
        <DialogContent>
          <DialogContentText>{phaseConfig?.confirmMessage}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <DialogTextActionButton onClick={() => setConfirmPhaseOpen(false)}>
            Annuler
          </DialogTextActionButton>
          <Button
            onClick={handlePhaseTransition}
            variant="contained"
            color={phaseConfig?.nextColor === "inherit" ? "primary" : phaseConfig?.nextColor}
          >
            Confirmer
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmCancelOpen} onClose={() => setConfirmCancelOpen(false)} maxWidth="sm">
        <DialogTitle>Confirmer l&apos;annulation du tournoi</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Cette action fermera les inscriptions pour tous (y compris les admins)
            et affichera le message d&apos;annulation sur la page d&apos;accueil.
            Les joueurs ne pourront plus s&apos;inscrire.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <DialogTextActionButton onClick={() => setConfirmCancelOpen(false)}>
            Retour
          </DialogTextActionButton>
          <Button
            onClick={handleCancelTournament}
            variant="contained"
            color="error"
          >
            Confirmer l&apos;annulation
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmRestoreOpen} onClose={() => setConfirmRestoreOpen(false)} maxWidth="sm">
        <DialogTitle>Rétablir le tournoi</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Le tournoi repassera en phase « Fermées aux joueurs ». Le message
            d&apos;annulation ne sera plus affiché sur la page d&apos;accueil.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <DialogTextActionButton onClick={() => setConfirmRestoreOpen(false)}>
            Annuler
          </DialogTextActionButton>
          <Button
            onClick={handleRestoreTournament}
            variant="contained"
            color="warning"
          >
            Rétablir
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackBarOpened}
        autoHideDuration={6000}
        anchorOrigin={{ horizontal: "center", vertical: "bottom" }}
        onClose={handleCloseSnackBar}
      >
        <Alert
          onClose={handleCloseSnackBar}
          severity={snackBarSeverity}
          sx={{ width: "100%" }}
        >
          {snackBarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default AdminTournament;
